// Putting a routine onto some days.
//
// A routine says what a day needs. This says which days. Two steps, like the
// week copy and for the same reason: a day that already has a plan should say
// so before it gets a second one, because a day planned twice reads as needing
// double the staff and nothing about it looks wrong.
//
// Days are picked, not assumed. "Regular Weekday" onto Saturday and Sunday is a
// mistake somebody makes once, and a dialog that silently took the whole week
// would make it every time.

import { useEffect, useState } from 'react';
import { fmtDate } from '../../lib/dates';

function dayLabel(iso) {
  return fmtDate(iso, 'dayNumeric');
}

/** Sat and Sun, which most routines are not meant for. */
function isWeekend(iso) {
  const d = new Date(iso + 'T12:00:00Z').getUTCDay();
  return d === 0 || d === 6;
}

export default function ApplyRoutineDialog({
  days,
  routines = [],
  busy,
  error,
  preview,
  onPreview,
  onConfirm,
  onClose,
}) {
  const [routineId, setRoutineId] = useState(routines[0]?.id || '');
  // Weekdays start ticked. A barn's default routine is a weekday routine, and
  // starting with Saturday selected is how it gets applied there by accident.
  const [picked, setPicked] = useState(() => new Set(days.filter((d) => !isWeekend(d))));
  const [replace, setReplace] = useState(false);

  const routine = routines.find((r) => r.id === routineId) || null;
  const dates = [...picked].sort();

  // Re-ask whenever the question changes, so the numbers on screen are always
  // the answer to what is currently selected.
  useEffect(() => {
    if (routineId && dates.length) onPreview({ routineId, dates, replace });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routineId, dates.join(','), replace]);

  const alreadyPlanned = (preview?.days ?? []).filter((d) => d.existing > 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-xl rounded-t-xl sm:rounded-xl overflow-hidden max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Apply a routine"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">Plan these days</h2>
            <p className="text-sm text-ink-500">
              A routine says what the barn needs. It doesn&apos;t put anybody on — it tells the roster
              when you&apos;re short.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {routines.length === 0 ? (
            <p className="text-sm text-ink-500">
              No routines yet. Build one under More → Routines — it&apos;s the list of what a day needs
              and how many people each job takes.
            </p>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="apply-routine">
                  Routine
                </label>
                <select
                  id="apply-routine"
                  className="input w-full"
                  value={routineId}
                  onChange={(e) => setRoutineId(e.target.value)}
                >
                  {routines.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} — {r.lines.length} {r.lines.length === 1 ? 'job' : 'jobs'}
                    </option>
                  ))}
                </select>
                {routine && (
                  <p className="text-xs text-ink-400 mt-1">
                    {routine.lines
                      .map((l) => `${l.typeName || 'Unspecified'} ×${l.peopleNeeded}`)
                      .join(' · ')}
                  </p>
                )}
              </div>

              <fieldset>
                <legend className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
                  Days
                </legend>
                <div className="flex flex-wrap gap-2">
                  {days.map((d) => {
                    const on = picked.has(d);
                    const planned = (preview?.days ?? []).find((x) => x.date === d)?.existing > 0;
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() =>
                          setPicked((prev) => {
                            const next = new Set(prev);
                            if (next.has(d)) next.delete(d);
                            else next.add(d);
                            return next;
                          })
                        }
                        aria-pressed={on}
                        className={
                          'px-3 py-1.5 rounded-full text-sm border ' +
                          (on
                            ? 'bg-hunter text-white border-hunter'
                            : 'bg-white text-ink-700 border-ink-200 hover:border-ink-300')
                        }
                      >
                        {dayLabel(d)}
                        {planned && <span className="ml-1 opacity-70">•</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 flex gap-3 text-xs">
                  <button type="button" className="text-ink-500 underline" onClick={() => setPicked(new Set(days))}>
                    All seven
                  </button>
                  <button
                    type="button"
                    className="text-ink-500 underline"
                    onClick={() => setPicked(new Set(days.filter((d) => !isWeekend(d))))}
                  >
                    Weekdays
                  </button>
                  <button type="button" className="text-ink-500 underline" onClick={() => setPicked(new Set())}>
                    None
                  </button>
                </div>
              </fieldset>

              {alreadyPlanned.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm text-amber-900">
                    {alreadyPlanned.length} of these {alreadyPlanned.length === 1 ? 'day is' : 'days are'}{' '}
                    already planned ({alreadyPlanned.map((d) => dayLabel(d.date)).join(', ')}).
                  </p>
                  <label className="mt-2 flex items-start gap-2 text-sm text-amber-900">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={replace}
                      onChange={(e) => setReplace(e.target.checked)}
                    />
                    <span>
                      Replace what&apos;s there.{' '}
                      <span className="text-amber-800">
                        {replace
                          ? `Clears ${preview?.willRemove ?? 0} existing ${(preview?.willRemove ?? 0) === 1 ? 'job' : 'jobs'} from those days first.`
                          : 'Otherwise this adds to them, and a day planned twice reads as needing twice the staff.'}
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {preview && dates.length > 0 && (
                <p className="text-sm text-ink-700">
                  Adds <strong>{preview.willAdd}</strong>{' '}
                  {preview.willAdd === 1 ? 'job' : 'jobs'} across {dates.length}{' '}
                  {dates.length === 1 ? 'day' : 'days'}. Nobody is scheduled and nobody is told
                  anything — this only says what the days need.
                </p>
              )}

              {error && (
                <p className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-ink-100 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !routineId || dates.length === 0}
            onClick={() => onConfirm({ routineId, dates, replace })}
          >
            {busy ? 'Planning…' : `Plan ${dates.length} ${dates.length === 1 ? 'day' : 'days'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
